import React from 'react';
import CanvasSection from './CanvasSection';
import FlowSection from './FlowSection';
import {
  EnquiryFormData,
  FlowDefinition,
  PageFlowState,
  SelectedVase,
} from '../../types';
import type { DesignSystemTypography } from '../../lib/typography';
import {
  isSectionLayoutMode,
  type SectionLayoutMode,
} from '../../lib/sectionLayoutMode';
import {
  normalizeCollectionCardTemplates,
  type CollectionCardTemplate,
} from '../../lib/collectionCardTemplates';
import {
  normalizeCanvasGridResolution,
  resolveCompactCanvasFloorRows,
  SECTION_MIN_ROWS_DEFAULT,
  SECTION_MIN_ROWS_MIN,
} from './layoutEngine/sectionSizing';
import { ensureResponsiveLayoutSlots } from './layoutEngine/blockFrame';
import { ensureResponsiveTransformSlots } from './layoutEngine/transformStyle';
import { convertLegacyEnquiryBlocks } from '../../lib/requestFormContent';
import { supportsFlowSectionRendering } from './flowSectionSupport';
import { isFlowSection } from '../../lib/sectionLayoutMode';
export { getHomepageSectionWrapperClasses } from './sectionWrapperClasses';

export type HomepageSectionType =
  | 'hero'
  | 'collection'
  | 'customiser'
  | 'enquiry'
  | 'subscribe'
  | 'about'
  | 'process'
  | 'events'
  | 'canvas';

export type CanvasSectionBlockType =
  | 'text'
  | 'image'
  | 'decorative-asset'
  | 'button'
  | 'video'
  | 'social'
  | 'site-progress'
  | 'site-footer'
  | 'gallery'
  | 'product-card'
  | 'model-viewer'
  | 'product-deconstructor'
  | 'request-form'
  | 'customiser-viewer'
  | 'customiser-finish-picker'
  | 'customiser-colour-filter'
  | 'customiser-combination-results'
  | 'spacer';
export const HOMEPAGE_CANVAS_COLUMNS = 24;

export interface CanvasSectionBlockLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type CanvasSectionResponsiveLayout = Partial<
  Record<'tablet' | 'mobile', CanvasSectionBlockLayout>
>;

export interface CanvasBlockLayoutChangeMeta {
  contentOffsetX?: number;
  contentOffsetY?: number;
  viewport?: 'desktop' | 'tablet' | 'mobile';
}

export interface CanvasSectionBlock {
  id: string;
  type: CanvasSectionBlockType;
  layout: CanvasSectionBlockLayout;
  responsiveLayout?: CanvasSectionResponsiveLayout;
  content?: Record<string, unknown>;
  style?: Record<string, unknown>;
}

export interface CanvasSectionSettings {
  minRows?: number;
  minRowsMode?: 'auto' | 'manual';
  presentation?: 'default' | 'immersive';
  blocks: CanvasSectionBlock[];
}

export type HomepageSectionPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';
export type HomepageSectionMaxWidth = 'full' | 'xl' | 'lg' | 'md';
export type HomepageSectionBackground = 'default' | 'muted' | 'accent';

export interface HomepageSectionSettings {
  builderLabel?: string;
  layoutMode?: SectionLayoutMode;
  progressStepId?: string;
  padding?: HomepageSectionPadding;
  paddingTop?: HomepageSectionPadding;
  paddingBottom?: HomepageSectionPadding;
  maxWidth?: HomepageSectionMaxWidth;
  background?: HomepageSectionBackground;
  builderSeedTemplate?: {
    kind: 'built-in';
    id: string;
    label: string;
  };
  builderTemplate?: {
    kind: 'saved';
    id: string;
  };
  canvas?: CanvasSectionSettings;
  collectionCardTemplates?: CollectionCardTemplate[];
}

export interface HomepageDesignSystemSettings extends Partial<DesignSystemTypography> {
  sectionSpacing?: HomepageSectionPadding;
  containerMaxWidth?: HomepageSectionMaxWidth;
  sectionBackground?: HomepageSectionBackground;
  mutedBackgroundColor?: string;
  accentBackgroundColor?: string;
  collectionCardTemplates?: CollectionCardTemplate[];
}

export interface HomepageLayoutSection {
  type: HomepageSectionType;
  id?: string;
  enabled?: boolean;
  settings?: HomepageSectionSettings;
}

export interface HomepageLayoutContent {
  version?: number;
  sections: HomepageLayoutSection[];
  flows?: FlowDefinition[];
  flowState?: PageFlowState;
}

export interface HomepageSectionRenderContext {
  onSelectVase: (vase: SelectedVase) => void;
  onEnquirySubmit: (formData: EnquiryFormData) => void;
  enquiryEmailTo: string;
  onNavigateToPage?: (href: string, meta?: { pageKey?: string }) => void;
  designSystemSettings?: HomepageDesignSystemSettings;
  canvasAdminPreview?: {
    enabled: boolean;
    viewport?: 'desktop' | 'tablet' | 'mobile';
    interactionMode?: 'none' | 'layout' | 'scale' | 'image';
    isImageAdjustMode?: boolean;
    activeAdjustBlockId?: string | null;
    onExitImageAdjust?: () => void;
    showPlacementGrid?: boolean;
    showGapGuides?: boolean;
    showLayoutFrames?: boolean;
    selectedBlockId?: string | null;
    selectedBlockIds?: string[];
    styleOverrides?: Record<string, Record<string, unknown> | undefined>;
    layoutOverrides?: Record<string, CanvasSectionBlockLayout | undefined>;
    showSelectionChrome?: boolean;
    onSelectBlock?: (blockId: string | null) => void;
    onOpenBlockContent?: (blockId: string, options?: { galleryItemId?: string }) => void;
    onBlockLayoutChange?: (
      blockId: string,
      layout: CanvasSectionBlockLayout,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onPreviewBlockLayout?: (
      blockId: string,
      layout: CanvasSectionBlockLayout | null,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onCommitBlockLayout?: (
      blockId: string,
      layout: CanvasSectionBlockLayout,
      meta?: CanvasBlockLayoutChangeMeta
    ) => void;
    onBlockScaleChange?: (blockId: string, scale: { x?: number; y?: number }) => void;
    onDeleteBlock?: (blockId: string) => void;
    onPreviewBlockStyle?: (blockId: string, styleOverride: Record<string, unknown> | null) => void;
    onCommitBlockStyle?: (
      blockId: string,
      styleOverride: Record<string, unknown> | null,
      commitViewport?: 'desktop' | 'tablet' | 'mobile'
    ) => void;
    onTextAutoHeightMeasured?: (blockId: string, rows: number) => void;
    onBlockAutoHeightMeasured?: (blockId: string, rows: number) => void;
    onProductCardDebugSnapshot?: (blockId: string | null, snapshot: Record<string, unknown> | null) => void;
    onConnectedBlockDebugSnapshot?: (snapshot: Record<string, unknown> | null) => void;
    isTransforming?: boolean;
    onTransformingChange?: (isTransforming: boolean) => void;
    placementDragRequest?: {
      requestId: number;
      primaryBlockId: string;
      blockIds: string[];
      referenceBlockId?: string;
      clientX?: number;
      clientY?: number;
    } | null;
    clearPlacementDragRequest?: () => void;
    onDuplicateCanvasBlock?: (blockId: string) => string | null;
    onDuplicateCanvasBlockForDrag?: (blockId: string) => CanvasSectionBlock | null;
    onPlacementCancel?: (blockIds: string[]) => void;
    onPlacementCommit?: (payload: {
      blockIds: string[];
      primaryBlockId: string;
      referenceBlockId?: string;
      deltaXPx: number;
      deltaYPx: number;
    }) => void;
  };
}

type SectionRenderer = (
  ctx: HomepageSectionRenderContext,
  section?: HomepageLayoutSection
) => React.ReactElement | null;

function renderCanvasLikeSection(
  ctx: HomepageSectionRenderContext,
  section?: HomepageLayoutSection
) {
  const sectionId = section?.id;
  const settings = section?.settings;
  const shouldUseFlowSection =
    !ctx.canvasAdminPreview?.enabled &&
    isFlowSection(section) &&
    supportsFlowSectionRendering(section);

  if (shouldUseFlowSection) {
    return (
      <FlowSection
        sectionId={sectionId}
        settings={settings}
        enquiryEmailTo={ctx.enquiryEmailTo}
        onSelectVase={ctx.onSelectVase}
        onEnquirySubmit={ctx.onEnquirySubmit}
      />
    );
  }

  return (
    <CanvasSection
      sectionId={sectionId}
      settings={settings}
      designSystemOverride={ctx.designSystemSettings}
      enquiryEmailTo={ctx.enquiryEmailTo}
      onSelectVase={ctx.onSelectVase}
      onEnquirySubmit={ctx.onEnquirySubmit}
      onNavigateToPage={ctx.onNavigateToPage}
      adminPreview={ctx.canvasAdminPreview}
    />
  );
}

export const HOMEPAGE_SINGLETON_SECTION_TYPES = new Set<HomepageSectionType>();

export function normalizeHomepageSectionSettings(
  settings: unknown,
  options?: {
    allowLegacyTwelveColumnUpscale?: boolean;
    sectionType?: HomepageSectionType;
  }
): HomepageSectionSettings | undefined {
  if (!settings || typeof settings !== 'object') return undefined;
  const raw = settings as Record<string, unknown>;
  const normalized: HomepageSectionSettings = {};

  if (typeof raw.builderLabel === 'string' && raw.builderLabel.trim()) {
    normalized.builderLabel = raw.builderLabel.trim();
  }

  if (isSectionLayoutMode(raw.layoutMode)) {
    normalized.layoutMode = raw.layoutMode;
  }

  if (typeof raw.progressStepId === 'string' && raw.progressStepId.trim()) {
    normalized.progressStepId = raw.progressStepId.trim();
  }

  if (raw.padding === 'none' || raw.padding === 'sm' || raw.padding === 'md' || raw.padding === 'lg' || raw.padding === 'xl') {
    normalized.padding = raw.padding;
  }

  if (raw.paddingTop === 'none' || raw.paddingTop === 'sm' || raw.paddingTop === 'md' || raw.paddingTop === 'lg' || raw.paddingTop === 'xl') {
    normalized.paddingTop = raw.paddingTop;
  }

  if (raw.paddingBottom === 'none' || raw.paddingBottom === 'sm' || raw.paddingBottom === 'md' || raw.paddingBottom === 'lg' || raw.paddingBottom === 'xl') {
    normalized.paddingBottom = raw.paddingBottom;
  }

  if (raw.maxWidth === 'full' || raw.maxWidth === 'xl' || raw.maxWidth === 'lg' || raw.maxWidth === 'md') {
    normalized.maxWidth = raw.maxWidth;
  }

  if (raw.background === 'default' || raw.background === 'muted' || raw.background === 'accent') {
    normalized.background = raw.background;
  }

  if (raw.builderTemplate && typeof raw.builderTemplate === 'object') {
    const builderTemplate = raw.builderTemplate as Record<string, unknown>;
    if (
      builderTemplate.kind === 'saved' &&
      typeof builderTemplate.id === 'string' &&
      builderTemplate.id.trim()
    ) {
      normalized.builderTemplate = {
        kind: 'saved',
        id: builderTemplate.id.trim(),
      };
    }
  }

  if (raw.builderSeedTemplate && typeof raw.builderSeedTemplate === 'object') {
    const builderSeedTemplate = raw.builderSeedTemplate as Record<string, unknown>;
    if (
      builderSeedTemplate.kind === 'built-in' &&
      typeof builderSeedTemplate.id === 'string' &&
      builderSeedTemplate.id.trim() &&
      typeof builderSeedTemplate.label === 'string' &&
      builderSeedTemplate.label.trim()
    ) {
      normalized.builderSeedTemplate = {
        kind: 'built-in',
        id: builderSeedTemplate.id.trim(),
        label: builderSeedTemplate.label.trim(),
      };
    }
  }

  if (raw.canvas && typeof raw.canvas === 'object') {
    const canvasRaw = raw.canvas as Record<string, unknown>;
    const rawBlocks = Array.isArray(canvasRaw.blocks) ? canvasRaw.blocks : [];
    const blocks: CanvasSectionBlock[] = rawBlocks
      .map((block): CanvasSectionBlock | null => {
        if (!block || typeof block !== 'object') return null;
        const blockRaw = block as Record<string, unknown>;
        const type = blockRaw.type;
        if (
          type !== 'text' &&
          type !== 'image' &&
          type !== 'decorative-asset' &&
          type !== 'button' &&
          type !== 'video' &&
          type !== 'social' &&
          type !== 'site-progress' &&
          type !== 'site-footer' &&
          type !== 'gallery' &&
          type !== 'product-card' &&
          type !== 'model-viewer' &&
          type !== 'product-deconstructor' &&
          type !== 'request-form' &&
          type !== 'customiser-viewer' &&
          type !== 'customiser-finish-picker' &&
          type !== 'customiser-colour-filter' &&
          type !== 'customiser-combination-results' &&
          type !== 'spacer'
        ) {
          return null;
        }

        const layoutRaw =
          blockRaw.layout && typeof blockRaw.layout === 'object'
            ? (blockRaw.layout as Record<string, unknown>)
            : {};
        const x = Number.isFinite(layoutRaw.x) ? Number(layoutRaw.x) : 1;
        const y = Number.isFinite(layoutRaw.y) ? Number(layoutRaw.y) : 1;
        const w = Number.isFinite(layoutRaw.w) ? Number(layoutRaw.w) : 4;
        const h = Number.isFinite(layoutRaw.h) ? Number(layoutRaw.h) : 2;
        const normalizeBlockLayout = (layout: unknown): CanvasSectionBlockLayout | null => {
          if (!layout || typeof layout !== 'object') return null;
          const layoutRecord = layout as Record<string, unknown>;
          const nextW = Number.isFinite(layoutRecord.w) ? Number(layoutRecord.w) : 4;
          let nextX = Number.isFinite(layoutRecord.x) ? Number(layoutRecord.x) : 1;
          const normalizedW = Math.max(1, Math.min(HOMEPAGE_CANVAS_COLUMNS, Math.round(nextW)));
          nextX = Math.max(1, Math.min(HOMEPAGE_CANVAS_COLUMNS, Math.round(nextX)));
          if (nextX + normalizedW - 1 > HOMEPAGE_CANVAS_COLUMNS) {
            nextX = Math.max(1, HOMEPAGE_CANVAS_COLUMNS - normalizedW + 1);
          }
          return {
            x: nextX,
            y: Math.max(1, Math.round(Number.isFinite(layoutRecord.y) ? Number(layoutRecord.y) : 1)),
            w: normalizedW,
            h: Math.max(1, Math.round(Number.isFinite(layoutRecord.h) ? Number(layoutRecord.h) : 2)),
          };
        };
        const responsiveLayoutRaw =
          blockRaw.responsiveLayout && typeof blockRaw.responsiveLayout === 'object'
            ? (blockRaw.responsiveLayout as Record<string, unknown>)
            : {};
        const tabletLayout = normalizeBlockLayout(responsiveLayoutRaw.tablet);
        const mobileLayout = normalizeBlockLayout(responsiveLayoutRaw.mobile);
        const responsiveLayout =
          tabletLayout || mobileLayout
            ? {
                ...(tabletLayout ? { tablet: tabletLayout } : {}),
                ...(mobileLayout ? { mobile: mobileLayout } : {}),
              }
            : undefined;

        return ensureResponsiveLayoutSlots({
          id:
            typeof blockRaw.id === 'string' && blockRaw.id.trim()
              ? blockRaw.id
              : `canvas-block-${Math.random().toString(36).slice(2, 9)}`,
          type,
          layout: {
            x: Math.max(1, Math.min(HOMEPAGE_CANVAS_COLUMNS, Math.round(x))),
            y: Math.max(1, Math.round(y)),
            w: Math.max(1, Math.min(HOMEPAGE_CANVAS_COLUMNS, Math.round(w))),
            h: Math.max(1, Math.round(h)),
          },
          responsiveLayout,
          content:
            blockRaw.content && typeof blockRaw.content === 'object'
              ? (blockRaw.content as Record<string, unknown>)
              : undefined,
          style: ensureResponsiveTransformSlots(
            blockRaw.style && typeof blockRaw.style === 'object'
              ? (blockRaw.style as Record<string, unknown>)
              : undefined
          ),
        });
      })
      .filter((block): block is CanvasSectionBlock => Boolean(block));

    const shouldUpscaleLegacyTwelveColumnLayout =
      options?.allowLegacyTwelveColumnUpscale !== false &&
      blocks.length > 0 &&
      blocks.every((block) => block.layout.x >= 1 && block.layout.x + block.layout.w - 1 <= 12);

    const maybeUpscaledBlocks = shouldUpscaleLegacyTwelveColumnLayout
      ? blocks.map((block) => ({
          ...block,
          layout: {
            ...block.layout,
            x: Math.max(
              1,
              Math.min(HOMEPAGE_CANVAS_COLUMNS, (block.layout.x - 1) * 2 + 1)
            ),
            w: Math.max(1, Math.min(HOMEPAGE_CANVAS_COLUMNS, block.layout.w * 2)),
          },
        }))
      : blocks;

    const normalizedBlocks =
      options?.sectionType === 'enquiry'
        ? convertLegacyEnquiryBlocks(maybeUpscaledBlocks)
        : maybeUpscaledBlocks;

    normalized.canvas = normalizeCanvasGridResolution(
      {
        minRows: canvasRaw.minRows,
        minRowsMode: canvasRaw.minRowsMode === 'manual' ? 'manual' : 'auto',
        presentation: canvasRaw.presentation === 'immersive' ? 'immersive' : 'default',
        blocks: normalizedBlocks,
      },
      {
        floorRows: resolveCompactCanvasFloorRows(normalizedBlocks, SECTION_MIN_ROWS_MIN),
        fallbackRows: resolveCompactCanvasFloorRows(normalizedBlocks, SECTION_MIN_ROWS_DEFAULT),
      }
    );
  }

  if (raw.collectionCardTemplates !== undefined) {
    const collectionCardTemplates = normalizeCollectionCardTemplates(raw.collectionCardTemplates);
    if (collectionCardTemplates.length > 0) {
      normalized.collectionCardTemplates = collectionCardTemplates;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export const DEFAULT_HOME_LAYOUT_CONTENT: HomepageLayoutContent = {
  version: 1,
  flows: [],
  flowState: {},
  sections: [
    { type: 'canvas' },
    { type: 'enquiry' },
  ],
};

export const HOMEPAGE_SECTION_REGISTRY: Record<HomepageSectionType, SectionRenderer> = {
  hero: () => null,
  collection: () => null,
  customiser: () => null,
  enquiry: renderCanvasLikeSection,
  subscribe: () => null,
  about: () => null,
  process: () => null,
  events: () => null,
  canvas: renderCanvasLikeSection,
};
